import { ProductSortConfig } from '@aboutyou/backbone/endpoints/products/products'
import { ProductSortingIdType, SortingChannels } from 'constants/sorting'
import { exists } from 'helpers/exists'
import { redirect } from 'helpers/navigation'
import { IService } from 'services'
import {
  IrisStoryDetailsNotFound,
  StoryRedirectResponse,
} from 'services/errors/stories'
import {
  CommonStoryRow,
  NormalizedLinkedProductResource,
  StoryRow,
} from 'services/providers/iris/entities/stories/storyRows/storyRow.normalizer.types'
import { PRODUCT_TILE_SET_EDITORIAL } from 'services/sets/product'
import { STORY_DETAIL_SET } from 'services/sets/story'
import { createProductStreamFetcher } from 'sharedComponents/ProductStream/loadingHelpers'
import AppStore from 'store'
import Product from 'store/entities/Product/Product.store'
import { SelectedLooseFilterMapType } from 'store/sidecar/FilterViewStore'
import PaginationViewStore from 'store/sidecar/PaginationViewStore'
import { enhanceWithStoryRowOutfits } from './enhanceWithStoryRowOutfits'

export async function loadStory({
  service,
  store,
  storyId,
}: {
  service: IService
  store: AppStore
  storyId: string
}) {
  const {
    associatedCategoryIds,
    introText,
    outfits,
    seo,
    story,
    idols,
    storyAttributeFilters,
    storyRows: normalizedStoryRows,
  } = await service.stories
    .requestByStoryId(storyId, STORY_DETAIL_SET)
    .catch(async error => {
      if (
        (error.response && error.response.status >= 500) ||
        error instanceof IrisStoryDetailsNotFound ||
        error instanceof StoryRedirectResponse
      ) {
        if (
          error instanceof IrisStoryDetailsNotFound &&
          store.urlManager.pathname.startsWith('/s/')
        ) {
          const storyRedirect = await service.redirect.requestRedirectByUrl(
            store.urlManager.pathname,
          )
          if (storyRedirect) {
            throw redirect(
              storyRedirect.target,
              {},
              { replaceState: true, status: 301 },
            )
          }
        }

        throw redirect('/', { notification: '1' }, { replaceState: true })
      }

      throw error
    })

  const isStoryOutfitRecoActive = store.feature.isActive(
    'enableStoryOutfitReco',
  )

  const storyRows = isStoryOutfitRecoActive
    ? await enhanceWithStoryRowOutfits(service, normalizedStoryRows)
    : normalizedStoryRows

  return {
    associatedCategoryIds,
    introText,
    outfits,
    seo,
    story,
    idols,
    storyAttributeFilters,
    storyRows,
  }
}

export async function loadFirstBatchAndStoryProducts({
  sortChannel,
  sortId,
  categoryId,
  sortingScore,
  service,
  isIdolStory,
  currentPage,
  selectedFilters,
  groupAttributeFilters,
  onError,
  isEditorialPresent,
  storyRows,
  forceAddProducts,
}: {
  sortId: ProductSortingIdType
  sortChannel: SortingChannels
  categoryId: string
  sortingScore: ProductSortConfig['score']
  service: IService
  currentPage: number
  selectedFilters: SelectedLooseFilterMapType
  groupAttributeFilters: Record<number, number[]>
  onError?: ((e: Error) => void) | undefined
  isEditorialPresent: boolean
  isIdolStory: boolean
  storyRows: Promise<StoryRow[]>
  forceAddProducts?: boolean
}) {
  const fetchProducts = createProductStreamFetcher({
    sortId,
    sortChannel,
    categoryId,
    service,
    filter: { ...selectedFilters, ...groupAttributeFilters },
    score: sortingScore,
    onError,
    forceAddProducts,
  })
  const currentBatch = isIdolStory
    ? 0
    : PaginationViewStore.pageToFirstBatch(currentPage)

  const productStreamRequest = isIdolStory
    ? Promise.resolve({
        pagination: { current: 0, last: 0, total: 0 },
        productIds: [],
        products: [],
      })
    : fetchProducts({
        perPage: PaginationViewStore.productsPerBatch,
        page: currentBatch,
      })

  const {
    additionalProductIds,
    storyRowLinkedProductIds,
    collectedStoryProductIds,
  } = isEditorialPresent
    ? await storyRows.then(getProductIdsFromStoryRows)
    : {
        additionalProductIds: new Set<string>(),
        storyRowLinkedProductIds: new Set<string>(),
        collectedStoryProductIds: [],
      }

  const collectedStoryProductsRequest =
    collectedStoryProductIds.length > 0
      ? service.products.requestProductsByIds(
          collectedStoryProductIds,
          PRODUCT_TILE_SET_EDITORIAL,
        )
      : Promise.resolve([])

  const [{ pagination, productIds }, collectedStoryProducts] =
    await Promise.all([productStreamRequest, collectedStoryProductsRequest])

  const activeAdditionalProducts = collectedStoryProducts.filter(
    product => product.isActive && additionalProductIds.has(product.id),
  )

  const storyRowLinkedProducts = collectedStoryProducts.filter(product =>
    storyRowLinkedProductIds.has(product.id),
  )

  const duplicatesCount = productIds.filter(productId =>
    activeAdditionalProducts.some(({ id }) => productId === id),
  ).length

  if (isEditorialPresent) {
    extendLinkedProductResources(await storyRows, storyRowLinkedProducts)
    removeInactiveProductsFromStoryRows({
      storyRows: await storyRows,
      activeAdditionalProducts,
    })
  }

  return {
    activeAdditionalProducts,
    pagination,
    currentBatch,
    duplicatesCount,
    productIds,
  }
}

function removeInactiveProductsFromStoryRows({
  activeAdditionalProducts,
  storyRows,
}: {
  activeAdditionalProducts: Product[]
  storyRows: StoryRow[]
}) {
  const activeProductIDs = new Set(
    activeAdditionalProducts.map(product => product.id),
  )
  for (const row of storyRows) {
    if (row.type !== 'idol-story-outfit-header') {
      row.productIds = row.productIds.filter(id => activeProductIDs.has(id))
    }
  }
}

function getStoryRowLinkedProductIds(storyRows: StoryRow[]) {
  return storyRows
    .filter(row => row.type !== 'idol-story-outfit-header')
    .flatMap(row => (row as CommonStoryRow).elementRows.flatMap(row => row))
    .map(storyElement => {
      if (
        (storyElement.type === 'image' || storyElement.type === 'video') &&
        storyElement.linkedResource?.entityType === 'product'
      ) {
        return storyElement.linkedResource.id
      }
    })
    .filter(exists)
}

function extendLinkedProductResources(
  storyRows: StoryRow[],
  products: Product[],
) {
  const productById = new Map<string, Product>()
  for (const product of products) {
    productById.set(product.id, product)
  }

  storyRows
    .filter(row => row.type !== 'idol-story-outfit-header')
    .flatMap(row => (row as CommonStoryRow).elementRows.flatMap(row => row))
    .forEach(storyElement => {
      if (
        (storyElement.type === 'image' || storyElement.type === 'video') &&
        storyElement.linkedResource?.entityType === 'product'
      ) {
        const product = productById.get(storyElement.linkedResource.id)
        if (product) {
          storyElement.linkedResource = mergeProductIntoLinkedResource(
            storyElement.linkedResource,
            product,
          )
        }
      }
    })
}

function mergeProductIntoLinkedResource(
  linkedResource: NormalizedLinkedProductResource,
  product: Product,
): NormalizedLinkedProductResource {
  return {
    ...linkedResource,
    url: product.url,
    brand: product.brandDetails,
  }
}

const getProductIdsFromStoryRows = (
  storyRows: StoryRow[],
): {
  additionalProductIds: Set<string>
  storyRowLinkedProductIds: Set<string>
  collectedStoryProductIds: string[]
} => {
  const additionalProductIds = new Set(
    storyRows
      .filter(row => row.type !== 'idol-story-outfit-header')
      .flatMap(row => (row as CommonStoryRow).productIds),
  )

  const storyRowLinkedProductIds = new Set(
    getStoryRowLinkedProductIds(storyRows),
  )

  const collectedStoryProductIds = [
    ...new Set([...additionalProductIds, ...storyRowLinkedProductIds]),
  ]

  return {
    additionalProductIds,
    storyRowLinkedProductIds,
    collectedStoryProductIds,
  }
}
